import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FAQS, FEATURES, NAV_LINKS, ROLES, STORIES } from "../constants";

const CONTAINER = "mx-auto w-full max-w-[1160px] px-6";

/** Sticky, blurred top navigation. */
function LandingNav() {
  return (
    <header className="border-border sticky top-0 z-50 border-b bg-white/95 backdrop-blur-sm">
      <div className={cn(CONTAINER, "flex h-[68px] items-center justify-between gap-6")}>
        <a href="#top" className="shrink-0 hover:no-underline">
          <img
            src="/zencube-logo-dark.png"
            alt="ZenCube · Talent Registry"
            className="block h-auto w-full max-w-[188px]"
          />
        </a>
        <nav className="flex items-center gap-8">
          <div className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="hover:text-primary text-[14px] text-[#5a6068] hover:no-underline"
              >
                {link.label}
              </a>
            ))}
          </div>
          <Button asChild className="min-w-[120px] px-6">
            <Link to="/login">Sign in</Link>
          </Button>
        </nav>
      </div>
    </header>
  );
}

/** Navy gradient hero, text right-aligned to match the nav (centered on mobile). */
function Hero() {
  return (
    <section
      id="top"
      className="bg-[linear-gradient(120deg,#0a1626_0%,#0e2742_55%,#123356_100%)] py-12 text-center text-white md:py-16 md:text-right"
    >
      <div className={cn(CONTAINER, "pt-10 pb-7")}>
        <div className="mb-6 flex flex-col items-center justify-center gap-3 md:flex-row md:justify-end md:gap-[22px]">
          <span className="text-[38px] leading-none font-semibold tracking-[4px] text-[#5d8ec4] md:text-[46px]">
            ZENCUBE
          </span>
          <span className="hidden h-[46px] w-px bg-white/35 md:block" />
          <span className="text-[28px] leading-none font-medium tracking-[0.5px] text-white md:text-[34px]">
            Talent Registry
          </span>
        </div>

        <p className="mb-6 text-[16px] font-medium tracking-[1px] text-[#8fb0d6] uppercase">
          A navigation system for human effort
        </p>

        <h1 className="mx-auto mb-4 max-w-[760px] text-[30px] leading-[1.25] font-semibold text-white md:mr-0 md:ml-auto md:text-[40px]">
          Connect student talent
          <br />
          with enterprise opportunity
        </h1>

        <p className="mx-auto mb-8 max-w-[640px] text-[18px] leading-[1.6] text-[#d4dce6] md:mr-0 md:ml-auto">
          Talent Registry pairs ZenCube student talent with enterprise opportunity through a
          transparent, HR-mediated hiring pipeline — every application moves forward with oversight.
        </p>

        <div className="flex flex-wrap justify-center md:justify-end">
          <Button asChild size="lg" className="min-w-[260px] px-8">
            <Link to="/login">Sign in</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

/** Eyebrow + heading + lede shown above each section. */
function SectionHead({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-8 max-w-full">
      <p className="text-title mb-2 text-[12px] font-bold tracking-[1px] uppercase">{eyebrow}</p>
      <h2 className="mb-2 text-[32px]">{title}</h2>
      <p className="text-muted-foreground max-w-[720px] text-[15.5px]">{children}</p>
    </div>
  );
}

/** Four role rows (full-width horizontal cards). */
function RolesSection() {
  return (
    <section id="roles" className="bg-background py-12 md:py-16">
      <div className={CONTAINER}>
        <SectionHead eyebrow="Who it's for" title="Built around four roles">
          Each role gets a purpose-built portal with exactly the access — and the limits — it needs.
        </SectionHead>

        <div className="flex flex-col gap-6">
          {ROLES.map((role) => (
            <article
              key={role.key}
              className="grid grid-cols-1 items-center gap-4 border border-[#3c83c9] p-6 md:min-h-[460px] md:grid-cols-[1fr_1.25fr] md:gap-11 md:p-12"
            >
              <div className="flex items-center justify-center">
                <img
                  src={role.image}
                  alt={role.alt}
                  loading="lazy"
                  className="block h-auto max-h-[280px] w-full max-w-[380px] object-contain mix-blend-multiply md:max-h-[440px]"
                />
              </div>
              <div className="flex flex-col py-2">
                <h3 className="text-primary mb-3 text-[22px] leading-[1.2] font-medium md:text-[28px]">
                  {role.title}
                </h3>
                <p className="text-paragraph mb-[18px] text-[15px] leading-[1.6]">{role.blurb}</p>
                <ul className="mb-[18px] list-none p-0">
                  {role.bullets.map((bullet) => (
                    <li key={bullet} className="text-heading py-1.5 text-[14.5px]">
                      {bullet}
                    </li>
                  ))}
                </ul>
                <Link
                  to={role.portalHref}
                  className="text-primary inline-block text-[14.5px] font-semibold"
                >
                  View the {role.title.split(" / ")[0]?.toLowerCase()} portal &rarr;
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Nine capability cards. */
function FeaturesSection() {
  return (
    <section id="features" className="py-12 md:py-16">
      <div className={CONTAINER}>
        <SectionHead eyebrow="Capabilities" title="Everything the hiring loop needs">
          From sourcing to selection, the platform covers each step — with the guardrails to match.
        </SectionHead>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div key={feature.title} className="border-border border bg-white px-7 py-[26px]">
                <div className="text-primary mb-4">
                  <Icon className="h-7 w-7" strokeWidth={1.7} />
                </div>
                <h3 className="mb-1.5 text-[16px]">{feature.title}</h3>
                <p className="text-muted-foreground text-[14px] leading-[1.55]">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Three illustrative success-story cards. */
function StoriesSection() {
  return (
    <section id="stories" className="bg-background py-12 md:py-16">
      <div className={CONTAINER}>
        <SectionHead eyebrow="Success stories" title="From ZenCube projects to first roles">
          Placements that began as a profile and a single application — managed end to end by HR.{" "}
          <em className="text-muted-foreground">Illustrative examples.</em>
        </SectionHead>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {STORIES.map((story) => (
            <div key={story.name} className="border-border flex flex-col border bg-white p-7">
              <p className="text-paragraph mb-6 flex-1 text-[15px] leading-[1.65]">
                <span className="text-brand-blue-light mr-1 align-[-0.4em] text-[40px] leading-[0]">
                  &ldquo;
                </span>
                {story.quote}
              </p>
              <div className="flex items-center gap-3 border-t border-[#f0f0f0] pt-4">
                <div className="text-heading flex h-11 w-11 shrink-0 items-center justify-center border border-[#8a9bb0] bg-white text-[14px] font-semibold">
                  {story.initials}
                </div>
                <div>
                  <div className="text-heading text-[14px] font-semibold">{story.name}</div>
                  <div className="text-muted-foreground text-[12.5px]">{story.school}</div>
                  <div className="text-muted-foreground mt-1 text-[12.5px]">
                    <span className="mr-1.5 inline-block bg-[#dceef3] px-2 py-0.5 text-[11px] font-semibold tracking-[0.4px] text-[#2d7787] uppercase">
                      Placed
                    </span>
                    {story.placement}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Accessible FAQ accordion (first item open by default). */
function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="py-12 md:py-16">
      <div className={CONTAINER}>
        <SectionHead eyebrow="Questions" title="Frequently asked">
          The essentials on how access, privacy, and the pipeline work.
        </SectionHead>

        <div>
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={faq.question} className="border-border -mb-px border bg-white">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => {
                    setOpenIndex(isOpen ? -1 : index);
                  }}
                  className="text-heading hover:text-primary flex w-full items-center justify-between gap-4 px-6 py-[18px] text-left text-[15.5px] font-semibold"
                >
                  {faq.question}
                  {isOpen ? (
                    <Minus className="text-primary h-5 w-5 shrink-0" />
                  ) : (
                    <Plus className="text-primary h-5 w-5 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="text-muted-foreground px-6 pb-5 text-[14.5px] leading-[1.65]">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Secure-access note band. */
function AuthNote() {
  return (
    <section className="bg-background py-12 md:py-16">
      <div className={CONTAINER}>
        <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-4 text-center text-[15px]">
          <span className="inline-block bg-[#dceef3] px-3 py-1 text-[11px] font-semibold tracking-[0.4px] text-[#2d7787] uppercase">
            Secure access
          </span>
          <span>
            Students sign in with <strong className="text-heading">ZenCube SSO</strong>; HR and
            enterprises use email — all protected by role-based permissions.
          </span>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="border-border border-t bg-white py-8">
      <div className={cn(CONTAINER, "flex flex-wrap items-center justify-between gap-6")}>
        <a href="#top" className="hover:no-underline">
          <img
            src="/zencube-logo-dark.png"
            alt="ZenCube · Talent Registry"
            className="block h-auto w-full max-w-[168px]"
          />
        </a>
        <div className="text-muted-foreground w-full text-center text-[13px] sm:w-auto sm:text-right">
          <p>&copy; 2026 ZenCube · Talent Registry. All rights reserved.</p>
          <p>
            Questions? Reach out to{" "}
            <a href="mailto:john@zenv.ai" className="text-primary">
              john@zenv.ai
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export function LandingPage() {
  return (
    <div className="bg-surface min-h-screen overflow-x-hidden scroll-smooth">
      <LandingNav />
      <main>
        <Hero />
        <RolesSection />
        <FeaturesSection />
        <StoriesSection />
        <FaqSection />
        <AuthNote />
      </main>
      <LandingFooter />
    </div>
  );
}
